import { useCallback, useMemo } from 'react';
import { useSettingsState } from '../components/SettingsModal/SettingsState';
import { isExpired } from '../lib/signer';
import { useActiveSigner } from './useActiveSigner';
import { useModalState } from './useModalState';

/**
 * Hook to handle expired signer state and provide a wrapper that checks signer validity
 * @returns Object containing signerExpired state and withSignerCheck wrapper
 */
export const useHandleExpiredSigner = () => {
  const activeSigner = useActiveSigner();
  const setSettingsModal = useModalState((state) => state.setSettingsModal);
  const pushPanel = useSettingsState(({ pushPanel }) => pushPanel);

  const signerExpired = isExpired(activeSigner?.data);

  /**
   * Wraps an action to check if signer is expired first
   * If expired, redirects to renew panel. Otherwise, executes the action.
   * @param action The action to execute if signer is valid
   */
  const withSignerCheck = useCallback(
    <TArgs extends unknown[], TReturn>(action: (...args: TArgs) => TReturn) => {
      return (...args: TArgs): TReturn | undefined => {
        if (signerExpired) {
          setSettingsModal('one-click');
          pushPanel('renew');
          return undefined;
        }
        return action(...args);
      };
    },
    [signerExpired, setSettingsModal, pushPanel]
  );

  return useMemo(() => ({ signerExpired, withSignerCheck }), [signerExpired, withSignerCheck]);
};
